/**
 * Catálogo de ARQUÉTIPOS DE TRABALHO e o classificador de cargo.
 *
 * O cargo em `extract_empregados` é texto livre e bagunçado (1.269 variantes:
 * "GARÇOM"/"GARCOM"/"CUMIM", "VENDEDOR (A)"/"VENDEDOR(A)"...). Em vez de casar
 * exato (top-200 cobre só 77% do headcount), classificamos por RAIZ/substring
 * sobre o texto normalizado (sem acento, maiúsculo) — o protótipo deste mapa
 * cobre ~96% do headcount real da base.
 *
 * Cada arquétipo dá ao avatar/HUD: um emblema (ícone phosphor), uma paleta
 * (gradiente + accent) e um rótulo legível. A ORDEM importa: o primeiro match
 * vence, então os específicos vêm antes dos genéricos (assistente/auxiliar).
 */
import {
  ForkKnife,
  Martini,
  CookingPot,
  Money,
  Storefront,
  MicrophoneStage,
  MusicNotes,
  Package,
  Truck,
  Scissors,
  Sparkle,
  ShieldCheck,
  FirstAid,
  PaintRoller,
  Code,
  Calculator,
  IdentificationCard,
  Briefcase,
  Factory,
  Wrench,
  Student,
  Megaphone,
  Handshake,
  Ruler,
  Crown,
  UserCircle,
  IconProps,
} from 'phosphor-react';

export interface JobArchetype {
  key: string;
  /** Rótulo curto da "classe" (mostrado no HUD). */
  label: string;
  Icon: React.ComponentType<IconProps>;
  /** Gradiente do emblema [claro, escuro]. */
  gradient: [string, string];
  /** Cor de destaque (anéis, chips, glow). */
  accent: string;
  /** Raízes/keywords já normalizadas (sem acento, MAIÚSCULO). */
  keywords: string[];
}

/** Remove acentos, sobe pra maiúsculo e colapsa espaços. */
export const normalizeCargo = (s?: string | null): string =>
  (s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const ARCHETYPES: JobArchetype[] = [
  {
    key: 'lideranca',
    label: 'Liderança',
    Icon: Crown,
    gradient: ['#F6B43C', '#E08A1E'],
    accent: '#D9820F',
    keywords: [
      'DIRETOR',
      'GERENTE',
      'COORDENADOR',
      'SUPERVISOR',
      'ENCARREGADO',
      'LIDER',
      'PRESIDENTE',
      'SOCIO',
      'PROPRIETARIO',
      'CHEFE DE EQUIPE',
      'HEAD ',
      'CEO',
    ],
  },
  {
    key: 'bar',
    label: 'Bar',
    Icon: Martini,
    gradient: ['#C77DFF', '#8B3FE8'],
    accent: '#8B3FE8',
    keywords: ['BARMAN', 'BARMAID', 'BARTENDER', 'BARISTA', 'SOMMELIER', 'CHOPP', 'BAR '],
  },
  {
    key: 'cozinha',
    label: 'Cozinha',
    Icon: CookingPot,
    gradient: ['#FF8A5B', '#E5532B'],
    accent: '#E5532B',
    keywords: [
      'COZINH',
      'CHEF',
      'CONFEITEIR',
      'PADEIR',
      'PIZZAIOL',
      'CHURRASQUEIR',
      'COPEIR',
      'SUSHI',
      'MASSEIR',
      'SALGADEIR',
      'CHAPEIR',
      'GRELHAD',
      'ACOUG',
    ],
  },
  {
    key: 'atendimento',
    label: 'Atendimento',
    Icon: ForkKnife,
    gradient: ['#3FA9F5', '#1B6FD6'],
    accent: '#1B6FD6',
    keywords: [
      'GARCOM',
      'GARCO',
      'CUMIM',
      'CUMM',
      'ATENDENTE',
      'ATENDIMENT',
      'RECEPCIONIST',
      'RECEPCAO',
      'HOSTESS',
      'HOSTER',
      'MAITRE',
      'COMANDA',
      'CHEFE DE FILA',
      'BALCONIST',
      'CAFETERIA',
      'ACOLHIMENTO',
      'CREDENCIAMENTO',
      'CHECK IN',
      'CHECK OUT',
      'CHECKIN',
      'RECEPTIVO',
      'MONITOR',
      'POSSO AJUDAR',
      'CONTINUO',
      'COMISSARI',
      'SAC',
      'TELEATEND',
      'CALL CENTER',
      'OPERADOR DE TELE',
      'CONCIERGE',
      'REL CLIENTE',
      'RELACIONAMENTO COM',
      'HOSPITALIDADE',
      'APONTADOR',
    ],
  },
  {
    key: 'caixa',
    label: 'Caixa',
    Icon: Money,
    gradient: ['#34D399', '#0E9F6E'],
    accent: '#0E9F6E',
    keywords: ['CAIXA', 'OPERADOR DE CAIXA', 'TESOUR', 'FRENTE DE CAIXA'],
  },
  {
    key: 'vendas',
    label: 'Vendas',
    Icon: Storefront,
    gradient: ['#5B8DEF', '#2F5FD0'],
    accent: '#2F5FD0',
    keywords: [
      'VEND',
      'COMERCIAL',
      'PROMOTOR',
      'REPRESENTANTE',
      'CORRETOR',
      'LOJA',
      'BALCAO',
      'SUCESSO DO CLIENTE',
      'NEGOCIO',
      'CONSULTOR',
      'PRE VENDA',
    ],
  },
  {
    key: 'eventos',
    label: 'Eventos',
    Icon: MicrophoneStage,
    gradient: ['#A78BFA', '#6D28D9'],
    accent: '#6D28D9',
    keywords: [
      'PALCO',
      'ILUMINA',
      'SONOPLAST',
      'CENOTEC',
      'CENOGRAF',
      'MONTAGEM',
      'MONT DE',
      'MONT ',
      'MONTADOR',
      'ESTRUTURA',
      'RIGGER',
      'ROADIE',
      'BACKSTAGE',
      'CAMARIM',
      'CAMAROTE',
      'PRODUTOR',
      'PRODUCAO DE EVENTO',
      'EVENTO',
      'FESTA',
      'OPERADOR DE LUZ',
      'OPERADOR DE SOM',
      'TECNICO DE SOM',
      'TECNICO DE PALCO',
      'FIGURANTE',
      'MOCHILEIRO',
      'STAFF',
      'BRINDE',
      'BUFFET',
      'GARCONETE',
    ],
  },
  {
    key: 'artistico',
    label: 'Artístico',
    Icon: MusicNotes,
    gradient: ['#F472B6', '#DB2777'],
    accent: '#DB2777',
    keywords: [
      'MUSIC',
      'CANTOR',
      ' DJ',
      'DJ ',
      'DANCAR',
      'ARTIST',
      'ATOR',
      'ATRIZ',
      'BAILARIN',
      'PERFORM',
      'INTERPRETE DE LIBRAS',
      'LIBRAS',
      'FOTOGRAF',
      'FILMMAKER',
      'CINEGRAF',
      'EDITOR DE VIDEO',
      'DESIGNER',
      'MAQUIADOR',
      'MAKE',
      'CABELE',
      'APRESENTAD',
      'LOCUTOR',
    ],
  },
  {
    key: 'logistica',
    label: 'Logística',
    Icon: Package,
    gradient: ['#FBBF24', '#D97706'],
    accent: '#D97706',
    keywords: [
      'CARGA',
      'DESCARGA',
      'ESTOQU',
      'ALMOXARIF',
      'EXPEDICAO',
      'SEPARADOR',
      'CONFERENTE',
      'EMPILHAD',
      'LOGISTIC',
      'DEPOSITO',
      'ARMAZEM',
      'REPOSITOR',
      'AJUDANTE DE CARGA',
    ],
  },
  {
    key: 'transporte',
    label: 'Transporte',
    Icon: Truck,
    gradient: ['#60A5FA', '#2563EB'],
    accent: '#2563EB',
    keywords: ['MOTORISTA', 'MOTOBOY', 'ENTREGAD', 'CONDUTOR', 'CHOFER', 'CAMINHONEIR', 'PILOTO'],
  },
  {
    key: 'textil',
    label: 'Têxtil',
    Icon: Scissors,
    gradient: ['#F0ABFC', '#C026D3'],
    accent: '#C026D3',
    keywords: [
      'COSTUR',
      'ALFAIATE',
      'MODELIST',
      'ESTILIST',
      'CORTADOR',
      'AJUSTAD',
      'AJUSTACAO',
      'PASSADEIR',
      'TECEL',
      'BORDAD',
      'CORTE DE MOLDE',
      'COSTUREIR',
      'MARCADOR',
    ],
  },
  {
    key: 'limpeza',
    label: 'Serviços gerais',
    Icon: Sparkle,
    gradient: ['#2DD4BF', '#0D9488'],
    accent: '#0D9488',
    keywords: [
      'LIMPEZA',
      'SERVICOS GERAIS',
      'SERVICO GERAL',
      'FAXIN',
      'ZELADOR',
      'JARDINEIR',
      'DOMESTIC',
      'CAMAREIR',
      'HIGIEN',
      'COPA',
      'LAVADOR',
      'ASG',
    ],
  },
  {
    key: 'seguranca',
    label: 'Segurança',
    Icon: ShieldCheck,
    gradient: ['#94A3B8', '#475569'],
    accent: '#475569',
    keywords: [
      'SEGURANC',
      'VIGILANT',
      'VIGIA',
      'PORTEIR',
      'CONTROLADOR DE ACESSO',
      'ACESSO',
      'BOMBEIRO',
      'BRIGADIST',
      'GUARDA',
    ],
  },
  {
    key: 'saude',
    label: 'Saúde',
    Icon: FirstAid,
    gradient: ['#FB7185', '#E11D48'],
    accent: '#E11D48',
    keywords: [
      'ENFERM',
      'MEDIC',
      'FARMAC',
      'NUTRICION',
      'FISIOTERAP',
      'DENTIST',
      'PSICOL',
      'SOCORRIST',
      'CUIDADOR',
      'TECNICO DE ENFERMAGEM',
      'BABA',
      'BABÁ',
    ],
  },
  {
    key: 'construcao',
    label: 'Construção',
    Icon: PaintRoller,
    gradient: ['#FCD34D', '#B45309'],
    accent: '#B45309',
    keywords: [
      'PEDREIR',
      'SERVENTE',
      'ELETRICIST',
      'ENCANAD',
      'PINTOR',
      'SOLDAD',
      'SERRALHEIR',
      'MARCENEIR',
      'CARPINTEIR',
      'GESSEIR',
      'AZULEJIST',
      'OBRA',
      'SERRA',
      'MECANIC',
      'TORNEIR',
      'FUNILEIR',
      'BORRACHEIR',
      'METALIC',
    ],
  },
  {
    key: 'engenharia',
    label: 'Engenharia',
    Icon: Ruler,
    gradient: ['#818CF8', '#4F46E5'],
    accent: '#4F46E5',
    keywords: [
      'ENGENHEIR',
      'ARQUITET',
      'PROJETIST',
      'DESENHIST',
      'MEDIDOR',
      'TOPOGRAF',
      'PROJETOS',
    ],
  },
  {
    key: 'ti',
    label: 'Tecnologia',
    Icon: Code,
    gradient: ['#22D3EE', '#0891B2'],
    accent: '#0891B2',
    keywords: [
      'DESENVOLV',
      'PROGRAMAD',
      'ANALISTA DE SISTEMA',
      'SUPORTE TECNICO',
      'ANALISTA SUPORTE',
      'ANALISTA DE SUPORTE',
      'INFRAESTRUT',
      'BANCO DE DADOS',
      'DEVOPS',
      'TESTER',
      'SCRUM',
      'FULL STACK',
      'FRONT END',
      'BACK END',
    ],
  },
  {
    key: 'financeiro',
    label: 'Financeiro',
    Icon: Calculator,
    gradient: ['#4ADE80', '#16A34A'],
    accent: '#16A34A',
    keywords: [
      'FINANCEIR',
      'CONTABIL',
      'CONTADOR',
      'FISCAL',
      'COBRANC',
      'FATURAMENT',
      'TESOURARIA',
      'CONTAS A PAGAR',
      'CONTAS A RECEBER',
      'AUDITOR',
    ],
  },
  {
    key: 'rh',
    label: 'RH',
    Icon: IdentificationCard,
    gradient: ['#F9A8D4', '#DB2777'],
    accent: '#BE185D',
    keywords: [
      'RECURSOS HUMANOS',
      'DEPARTAMENTO PESSOAL',
      'RECRUT',
      'FOLHA DE PAGAMENTO',
      'TREINAMENT',
      'DEPTO PESSOAL',
      ' RH',
      'RH ',
    ],
  },
  {
    key: 'administrativo',
    label: 'Administrativo',
    Icon: Briefcase,
    gradient: ['#7DD3FC', '#0284C7'],
    accent: '#0284C7',
    keywords: [
      'ADMINISTRAT',
      'ADMINISTRA',
      'SECRETARI',
      'ESCRIT',
      'BACK OFFICE',
      'ARQUIV',
      'ASSESSOR',
      'GESTAO',
      'PARALEGAL',
      'JURIDIC',
      'ADVOG',
      'EMPRESARIO',
    ],
  },
  {
    key: 'producao',
    label: 'Produção',
    Icon: Factory,
    gradient: ['#A8A29E', '#57534E'],
    accent: '#57534E',
    keywords: [
      'OPERADOR DE PRODUCAO',
      'OPERADOR DE MAQUINA',
      'OP DE MAQUINA',
      'PRODUCAO',
      'INDUSTRI',
      'ENVAS',
      'EMBALAG',
      'LINHA DE PRODUCAO',
      'MANUFATUR',
      'FABRICA',
    ],
  },
  {
    key: 'tecnico',
    label: 'Técnico',
    Icon: Wrench,
    gradient: ['#5EEAD4', '#0F766E'],
    accent: '#0F766E',
    keywords: ['TECNIC', 'ELETRONIC', 'MANUTENCAO', 'INSTALAD', 'REPARAD'],
  },
  {
    key: 'estagio',
    label: 'Estágio / Aprendiz',
    Icon: Student,
    gradient: ['#FDE047', '#CA8A04'],
    accent: '#CA8A04',
    keywords: ['ESTAGI', 'TRAINEE', 'JOVEM APRENDIZ', 'APRENDIZ', 'MENOR APRENDIZ'],
  },
  {
    key: 'marketing',
    label: 'Marketing',
    Icon: Megaphone,
    gradient: ['#FB923C', '#EA580C'],
    accent: '#EA580C',
    keywords: ['MARKETING', 'MIDIAS SOCIAIS', 'SOCIAL MEDIA', 'PUBLICID', 'COMUNICAC', 'TRAFEGO'],
  },
  {
    key: 'autonomo',
    label: 'Autônomo',
    Icon: Handshake,
    gradient: ['#CBD5E1', '#64748B'],
    accent: '#64748B',
    keywords: ['AUTONOMO', 'FREELANC', 'PRESTADOR DE SERVICO', 'TERCEIRIZ'],
  },
  // Genérico (último): assistente/auxiliar/ajudante "puros", sem área detectada.
  {
    key: 'geral',
    label: 'Apoio geral',
    Icon: UserCircle,
    gradient: ['#9CA3AF', '#6B7280'],
    accent: '#6B7280',
    keywords: [
      'ASSISTENTE',
      'AUXILIAR',
      'AJUDANTE',
      'AUX ',
      'AGENTE',
      'OPERADOR',
      'COLABORADOR',
      'FUNCIONARIO',
      'GERAL',
      'POLIVALENTE',
    ],
  },
];

/** Fallback usado quando nada casa (mantém o avatar sempre renderizável). */
export const FALLBACK_ARCHETYPE: JobArchetype = {
  key: 'indefinido',
  label: 'Não classificado',
  Icon: UserCircle,
  gradient: ['#D1D5DB', '#9CA3AF'],
  accent: '#9CA3AF',
  keywords: [],
};

const BY_KEY = new Map(ARCHETYPES.map(a => [a.key, a]));

/** Classifica um cargo livre no seu arquétipo (primeiro match por raiz vence). */
export function classifyCargo(cargo?: string | null): JobArchetype {
  const c = ` ${normalizeCargo(cargo)} `;
  if (c.trim() === '') return FALLBACK_ARCHETYPE;
  return ARCHETYPES.find(arch => arch.keywords.some(kw => c.includes(kw))) ?? FALLBACK_ARCHETYPE;
}

export const archetypeByKey = (key: string): JobArchetype => BY_KEY.get(key) ?? FALLBACK_ARCHETYPE;

export type GenderKey = 'M' | 'F' | 'N';

/** Normaliza o `sexo` (M/F) em chave de avatar; demais/vazio → neutro. */
export function genderKey(sexo?: string | null): GenderKey {
  const s = (sexo ?? '').trim().toUpperCase();
  if (s.startsWith('M')) return 'M';
  if (s.startsWith('F')) return 'F';
  return 'N';
}

/** Capitaliza um cargo bagunçado pra exibição ("GARÇOM (A)" → "Garçom"). */
export function prettyCargo(cargo?: string | null): string {
  const raw = (cargo ?? '').trim();
  if (!raw) return 'Sem cargo';
  return raw
    .toLowerCase()
    .replace(/\s*\((a|o|os|as)\)\s*/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/(^|\s|\/|-)([a-zà-ú])/g, (_, p, ch) => p + ch.toUpperCase());
}
